import { FC, useEffect, useState } from 'react'
import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { checklistReservaService, mensagemErroChecklistReserva } from '@/services/checklistReservaService'
import type { SolicitacaoDocumentoReserva } from '@/types/checklist'
import type { DocumentoTipo } from '@/types/documento'
import { TIPOS_DOCUMENTO } from '@/utils/docsObrigatorios'

const MAX_ROTULO = 120
const MAX_TEXTO = 500

export interface CategoriaOpcao {
  codigo: string
  rotulo: string
}

interface Props {
  open: boolean
  reservaId: string
  /** Categorias da versão presa (código + rótulo) — vazio = só "Solicitados pela Curadoria". */
  categorias: CategoriaOpcao[]
  onFechar: () => void
  onSolicitado: (solicitacao: SolicitacaoDocumentoReserva) => void
}

/**
 * "Solicitar documento" da Curadoria (Q70, AN-PP3 §2.5/§6.2) — back-office. Cria o item `sol-N`
 * na reserva (não na versão do produto): aparece no checklist do consultor, que envia o arquivo
 * com `exigenciaChave = sol-N`. Obrigatório por padrão (bloqueia a volta da pendência e o
 * encaminhamento). Só com a reserva enviada e não terminal (409 `SOLICITACAO_FORA_DA_JANELA`).
 */
export const SolicitarDocumentoDialog: FC<Props> = ({ open, reservaId, categorias, onFechar, onSolicitado }) => {
  const [rotulo, setRotulo] = useState('')
  const [tipo, setTipo] = useState<DocumentoTipo>('OUTRO')
  const [categoria, setCategoria] = useState('')
  const [obrigatorio, setObrigatorio] = useState(true)
  const [orientacao, setOrientacao] = useState('')
  const [motivo, setMotivo] = useState('')
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setRotulo('')
      setTipo('OUTRO')
      setCategoria('')
      setObrigatorio(true)
      setOrientacao('')
      setMotivo('')
      setErro(null)
    }
  }, [open])

  const podeSalvar =
    !salvando &&
    rotulo.trim().length > 0 &&
    rotulo.length <= MAX_ROTULO &&
    motivo.trim().length > 0 &&
    motivo.length <= MAX_TEXTO &&
    orientacao.length <= MAX_TEXTO

  const salvar = async () => {
    if (!podeSalvar) return
    setSalvando(true)
    setErro(null)
    try {
      onSolicitado(
        await checklistReservaService.solicitar(reservaId, {
          rotulo: rotulo.trim(),
          tipoDocumento: tipo,
          categoria: categoria || null,
          obrigatorio,
          orientacao: orientacao.trim() || null,
          motivo: motivo.trim(),
        })
      )
    } catch (e) {
      setErro(mensagemErroChecklistReserva(e, 'Não foi possível solicitar o documento.'))
    } finally {
      setSalvando(false)
    }
  }

  return (
    <Dialog open={open} onClose={salvando ? undefined : onFechar} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Solicitar documento</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          O pedido entra no checklist desta reserva e aparece para o consultor. Ele não altera o
          checklist do produto.
        </Typography>
        <Stack spacing={2}>
          <TextField
            label="Documento"
            value={rotulo}
            onChange={(e) => setRotulo(e.target.value)}
            required
            fullWidth
            disabled={salvando}
            error={rotulo.length > MAX_ROTULO}
            helperText={`${rotulo.length}/${MAX_ROTULO}`}
          />
          <TextField
            select
            label="Tipo do documento"
            value={tipo}
            onChange={(e) => setTipo(e.target.value as DocumentoTipo)}
            fullWidth
            disabled={salvando}
          >
            {TIPOS_DOCUMENTO.map((t) => (
              <MenuItem key={t} value={t}>
                {t}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Categoria"
            value={categoria}
            onChange={(e) => setCategoria(e.target.value)}
            fullWidth
            disabled={salvando}
          >
            <MenuItem value="">Solicitados pela Curadoria</MenuItem>
            {categorias.map((c) => (
              <MenuItem key={c.codigo} value={c.codigo}>
                {c.rotulo}
              </MenuItem>
            ))}
          </TextField>
          <FormControlLabel
            control={
              <Checkbox checked={obrigatorio} onChange={(e) => setObrigatorio(e.target.checked)} disabled={salvando} />
            }
            label="Obrigatório (bloqueia a volta da pendência e o encaminhamento)"
          />
          <TextField
            label="Orientação ao consultor (opcional)"
            value={orientacao}
            onChange={(e) => setOrientacao(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            disabled={salvando}
            error={orientacao.length > MAX_TEXTO}
            helperText={`${orientacao.length}/${MAX_TEXTO}`}
          />
          <TextField
            label="Motivo da solicitação"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            required
            fullWidth
            multiline
            minRows={2}
            disabled={salvando}
            error={motivo.length > MAX_TEXTO}
            helperText={`Obrigatório — ${motivo.length}/${MAX_TEXTO}`}
          />
        </Stack>
        {erro && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {erro}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onFechar} disabled={salvando}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={() => void salvar()}
          disabled={!podeSalvar}
          startIcon={salvando ? <CircularProgress size={14} color="inherit" /> : undefined}
        >
          Solicitar
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default SolicitarDocumentoDialog
